import React, { useState } from 'react';
import { Pill } from './NotionUI';
import { optionColor } from './ProjectCell';
import taskValues from './project/TaskValues';
import { useLanguage } from '../i18n/LanguageContext';

const MAX_CARD_VALUES = 4;

/**
 * Vista "Per stato": una colonna per ogni opzione della colonna di
 * raggruppamento (più "Senza valore" se serve). Le card si trascinano da una
 * colonna all'altra e cambiano il valore della colonna di raggruppamento.
 */
function ProjectBoard({ columns, tasks, groupColumn, users, onMoveCard, onAdd, onOpen }) {
  const { t } = useLanguage();
  const [dragOver, setDragOver] = useState(null);
  const [cardMark, setCardMark] = useState(null); // card davanti alla quale si sta rilasciando

  const groupKey = String(groupColumn.id);
  const lanes = groupColumn.options.map((option) => ({ key: option.value, option }));
  const unassigned = tasks.filter((task) => !groupColumn.options.some((o) => o.value === task.data[groupKey]));
  if (unassigned.length > 0) lanes.push({ key: '', option: null });

  const tasksOf = (laneKey) => (
    laneKey === ''
      ? unassigned
      : tasks.filter((task) => task.data[groupKey] === laneKey)
  );

  // Valori secondari mostrati sulla card (tutto tranne titolo e colonna di raggruppamento).
  const cardValues = (task) => taskValues(task, columns, { users, allTasks: tasks, exclude: [groupColumn.id], max: MAX_CARD_VALUES });

  return (
    <div className="kanban-board">
      {lanes.map(({ key, option }) => {
        const laneTasks = tasksOf(key);
        return (
          <div
            key={key || 'none'}
            className={'kanban-column' + (dragOver === key ? ' drag-over' : '')}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(key);
            }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(null);
              const id = Number(e.dataTransfer.getData('text/plain'));
              if (id) onMoveCard(id, null, key || null);
            }}
          >
            <div className="kanban-column-header">
              {option ? <Pill dot color={optionColor(option)}>{option.label}</Pill> : <span className="nt-muted">{t('projectDetail.noValue')}</span>}
              <span className="kanban-count">{laneTasks.length}</span>
            </div>
            <div className="kanban-column-body">
              {laneTasks.map((task) => (
                <div
                  key={task.id}
                  className={'kanban-card' + (cardMark === task.id ? ' kanban-card-drop' : '')}
                  draggable
                  tabIndex={0}
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', String(task.id))}
                  onDragEnd={() => setCardMark(null)}
                  onDragOver={(e) => { e.preventDefault(); setCardMark(task.id); }}
                  onDragLeave={() => setCardMark((m) => (m === task.id ? null : m))}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCardMark(null);
                    setDragOver(null);
                    const id = Number(e.dataTransfer.getData('text/plain'));
                    if (id && id !== task.id) onMoveCard(id, task.id, key || null);
                  }}
                  onClick={() => onOpen(task)}
                  onKeyDown={(e) => e.key === 'Enter' && onOpen(task)}
                >
                  <div className="kanban-card-title">{task.title || t('projectDetail.untitled')}</div>
                  <div className="kanban-card-meta">
                    {cardValues(task)}
                    {task.tickets && task.tickets.length > 0 && (
                      <span className="nt-muted pd-card-value" title={t('projectDetail.linkedTickets')}>🎫 {task.tickets.length}</span>
                    )}
                  </div>
                </div>
              ))}
              <button type="button" className="nt-add-row" onClick={() => onAdd(key || null)}>
                <span aria-hidden="true">+</span> {t('projectDetail.newTask')}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ProjectBoard;
